/* Boonsoir 2.0 — Smaakwijzer. Shows one question at a time and reveals a server-rendered result.
   Without JS the form simply submits to the coffee collection. */
(() => {
  if (customElements.get('smaak-wijzer')) return;

  class SmaakWijzer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.form = this.querySelector('[data-sw-form]');
      this.steps = [...this.querySelectorAll('[data-sw-step]')];
      this.progress = this.querySelector('[data-sw-progress]');
      this.current = this.querySelector('[data-sw-current]');
      this.bars = [...this.querySelectorAll('.sw__bar span')];
      this.back = this.querySelector('[data-sw-back]');
      this.next = this.querySelector('[data-sw-next]');
      this.nextLabel = this.querySelector('[data-sw-next-label]');
      this.results = [...this.querySelectorAll('[data-sw-result]')];
      this.again = this.querySelector('[data-sw-again]');
      this.hint = this.querySelector('[data-sw-hint]');
      if (!this.form || !this.steps.length) return;

      this.classList.add('sw-enhanced');
      this.progress.hidden = false;
      this.index = 0;
      this.show(0, false);

      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.advance(); });
      this.back.addEventListener('click', () => this.show(this.index - 1, true));
      this.form.addEventListener('change', () => this.sync());
      // Pointer taps on an option move on by themselves; keyboard users choose with arrows and confirm with the button.
      this.form.addEventListener('click', (e) => {
        const input = e.target.closest('input[type="radio"]');
        if (!input || e.detail === 0) return;
        clearTimeout(this._t);
        this._t = setTimeout(() => this.advance(), 420);
      });
      const restart = this.querySelector('[data-sw-restart]');
      if (restart) restart.addEventListener('click', () => this.reset());
    }

    value(name) {
      const el = this.form.querySelector(`input[name="${name}"]:checked`);
      return el ? el.value : null;
    }

    answered(i) {
      return !!this.steps[i].querySelector('input:checked');
    }

    sync() {
      const ok = this.answered(this.index);
      this.next.setAttribute('aria-disabled', ok ? 'false' : 'true');
      this.next.classList.toggle('is-waiting', !ok);
      if (ok && this.hint) this.hint.hidden = true;
    }

    // Moving between questions: the current one slides out to the side it came from, the next comes in.
    slide(from, to, dir) {
      const fx = window.BoonsoirFX;
      const calm = !from || !to || from === to || (fx ? fx.still() : window.matchMedia('(prefers-reduced-motion: reduce)').matches) || !from.animate;
      const ease = 'cubic-bezier(.2,0,0,1)';
      if (this._out) { this._out.cancel(); this._out = null; }
      if (calm) { this.steps.forEach((s) => { s.hidden = s !== to; }); return; }
      this.steps.forEach((s) => { if (s !== from && s !== to) s.hidden = true; });
      to.hidden = true;
      this._out = from.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateX(${-24 * dir}px)` }], { duration: 220, easing: ease });
      this._out.onfinish = () => {
        this._out = null;
        from.hidden = true;
        to.hidden = false;
        to.animate([{ opacity: 0, transform: `translateX(${24 * dir}px)` }, { opacity: 1, transform: 'none' }], { duration: 420, easing: ease });
        if (this._pendingFocus) { const f = this._pendingFocus; this._pendingFocus = null; f.focus({ preventScroll: true }); }
      };
    }

    show(i, focus) {
      const prev = this.steps[this.index];
      const dir = i < this.index ? -1 : 1;
      this.index = Math.max(0, Math.min(i, this.steps.length - 1));
      this._pendingFocus = null;
      if (this._started) this.slide(prev, this.steps[this.index], dir);
      else this.steps.forEach((s, n) => { s.hidden = n !== this.index; });
      this._started = true;
      this.current.textContent = String(this.index + 1);
      this.bars.forEach((b, n) => b.classList.toggle('is-on', n <= this.index));
      this.back.hidden = this.index === 0;
      this.nextLabel.textContent = this.index === this.steps.length - 1 ? 'Toon mijn Boonsoir' : 'Volgende';
      this.sync();
      if (focus) {
        const target = this.steps[this.index].querySelector('input:checked') || this.steps[this.index].querySelector('input');
        if (target) {
          if (this.steps[this.index].hidden) this._pendingFocus = target;
          else target.focus({ preventScroll: true });
        }
      }
    }

    advance() {
      if (!this.answered(this.index)) {
        if (this.hint) this.hint.hidden = false;
        const first = this.steps[this.index].querySelector('input');
        if (first) first.focus({ preventScroll: true });
        return;
      }
      if (this.index < this.steps.length - 1) { this.show(this.index + 1, true); return; }
      this.reveal();
    }

    reveal() {
      const drink = this.value('drink');
      const smaak = this.value('smaak') || 'trio';
      const zet = this.value('zet');
      let shown = null;
      this.results.forEach((r) => {
        const on = r.dataset.swResult === smaak;
        r.hidden = !on;
        if (on) shown = r;
      });
      if (!shown) shown = this.results.find((r) => r.dataset.swResult === 'trio');
      if (!shown) return;
      shown.hidden = false;
      shown.classList.remove('is-entering');
      void shown.offsetWidth;
      shown.classList.add('is-entering');
      clearTimeout(this._enter);
      this._enter = setTimeout(() => shown.classList.remove('is-entering'), 2000);
      // The whole card takes the label colour of the outcome (poured from below, see the section CSS).
      this.dataset.swTint = smaak;
      this.classList.add('is-tinted');
      shown.querySelectorAll('[data-sw-drink]').forEach((el) => { el.hidden = el.dataset.swDrink !== drink; });
      shown.querySelectorAll('[data-sw-zet]').forEach((el) => { el.hidden = el.dataset.swZet !== zet; });
      this.form.hidden = true;
      if (this.again) this.again.hidden = false;
      // Make the outcome measurable: a hash for analytics/landing and an event a pixel can listen to.
      try { history.replaceState(history.state, '', '#smaakwijzer-' + smaak); } catch (err) { /* ignore */ }
      document.dispatchEvent(new CustomEvent('smaakwijzer:result', { detail: { result: smaak, drink, zet } }));
      const heading = shown.querySelector('[data-sw-focus]');
      if (heading) heading.focus({ preventScroll: true });
      const top = this.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.5) {
        const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h'), 10) || 72;
        window.scrollTo({ top: window.scrollY + top - header - 16, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }

    reset() {
      this.form.reset();
      this._started = false;
      this.classList.remove('is-tinted'); // the colour drains back down; the tint value stays until the next outcome
      clearTimeout(this._untint);
      this._untint = setTimeout(() => { if (!this.classList.contains('is-tinted')) delete this.dataset.swTint; }, 700);
      this.results.forEach((r) => { r.hidden = true; });
      if (this.again) this.again.hidden = true;
      this.form.hidden = false;
      this.show(0, true);
    }
  }

  customElements.define('smaak-wijzer', SmaakWijzer);
})();
